<template>
  <div class="ppp">
    <div v-if="loading" class="ppp-state">
      <span class="ppp-spinner"></span>
    </div>

    <div v-else-if="error" class="ppp-state ppp-state--error">{{ error }}</div>

    <div v-else-if="pathways.length === 0" class="ppp-state">
      No pathways are assigned to this user yet.
    </div>

    <template v-else>
      <!-- One card per result. A pathway counts once here however many
           modules it holds, which is what the P/O/F badge counts too. -->
      <div class="ppp-cards">
        <div class="ppp-card">
          <div class="ppp-card__label"><span class="ppp-dot ppp-dot--pass"></span>Completed Pathways</div>
          <div class="ppp-card__value">{{ summary.pass }}</div>
        </div>
        <div class="ppp-card">
          <div class="ppp-card__label"><span class="ppp-dot ppp-dot--open"></span>Open Pathways</div>
          <div class="ppp-card__value">{{ summary.open }}</div>
        </div>
        <div class="ppp-card">
          <div class="ppp-card__label"><span class="ppp-dot ppp-dot--fail"></span>Failed Pathways</div>
          <div class="ppp-card__value">{{ summary.fail }}</div>
        </div>
      </div>

      <div class="ppp-head">
        <div>
          <h4 class="ppp-head__title">Pathway progress</h4>
          <p class="ppp-head__sub">Each pathway counts once, whatever its module count.</p>
        </div>
        <span class="ppp-head__meta">Modules completed</span>
      </div>

      <ul class="ppp-list">
        <li v-for="pathway in pathways" :key="pathway.id" class="ppp-row">
          <button
            type="button"
            class="ppp-row__head"
            :aria-expanded="isOpen(pathway.id) ? 'true' : 'false'"
            @click="toggle(pathway.id)">
            <svg
              class="ppp-chev"
              :class="{ 'ppp-chev--open': isOpen(pathway.id) }"
              viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
              stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>

            <span class="ppp-row__main">
              <span class="ppp-row__title ignore-translation">{{ pathway.title }}</span>
              <span class="ppp-row__sub">
                {{ pathway.module_count }} module{{ pathway.module_count === 1 ? '' : 's' }}
                <template v-if="pathway.enrolled_at">
                  · assigned {{ shortDate(pathway.enrolled_at) }}
                </template>
              </span>
            </span>

            <span class="ppp-row__progress">
              <span class="ppp-row__counts">
                {{ pathway.completed_count }} of {{ pathway.module_count }} modules
                <strong>{{ pathway.progress_pct }}%</strong>
              </span>
              <span class="ppp-bar">
                <span
                  class="ppp-bar__fill"
                  :class="'ppp-bar__fill--' + pathway.result"
                  :style="{ width: pathway.progress_pct + '%' }"></span>
              </span>
            </span>

            <span class="ppp-pill" :class="'ppp-pill--' + pathway.result">
              <span class="ppp-dot" :class="'ppp-dot--' + pathway.result"></span>
              {{ pathway.result_label }}
            </span>
          </button>

          <div v-if="isOpen(pathway.id)" class="ppp-modules">
            <div v-if="!pathway.modules || pathway.modules.length === 0" class="ppp-modules__empty">
              This pathway has no modules yet.
            </div>
            <div v-else class="ppp-modules__scroll">
              <table class="ppp-table">
                <thead>
                  <tr>
                    <th class="ppp-table__seq">#</th>
                    <th>Module</th>
                    <th>Status</th>
                    <th>Completed</th>
                    <th>Assigned</th>
                    <th>Due</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(mod, idx) in pathway.modules" :key="mod.id" :class="{ 'ppp-row--removed': mod.removed_from_pathway }">
                    <td class="ppp-table__seq">{{ idx + 1 }}</td>
                    <td class="ignore-translation">
                      {{ mod.course_name || ('Course #' + mod.course_id) }}
                      <!-- Taken out of the pathway after this learner
                           completed it, so it stays on their record. -->
                      <span v-if="mod.removed_from_pathway" class="ppp-removed ignore-translation notranslate" translate="no">
                        No longer in this pathway
                      </span>
                    </td>
                    <td>
                      <span class="ppp-tag" :class="'ppp-tag--' + moduleResult(mod)">
                        {{ mod.course_status_label || 'Not started' }}
                      </span>
                    </td>
                    <td>{{ shortDate(mod.completed_at) }}</td>
                    <td>{{ shortDate(mod.assigned_at) }}</td>
                    <td>{{ shortDate(mod.due_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </li>
      </ul>

      <div class="ppp-legend">
        <span><span class="ppp-dot ppp-dot--pass"></span>All modules passed</span>
        <span><span class="ppp-dot ppp-dot--open"></span>At least one module still open</span>
        <span><span class="ppp-dot ppp-dot--fail"></span>A module failed or expired</span>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment';

/**
 * The Pathways tab of the admin P/O/F modal: every pathway assigned to one
 * user, with its progress and its single result, and a drop-down per pathway
 * listing the modules and where each one stands.
 *
 * A pathway is one unit of work here, matching the P/O/F badge on the user
 * list; the per-occurrence course rows stay on the Courses tab beside it.
 */
export default {
  name: 'PathwayProgressPanel',
  props: {
    /** tbl_employee id whose pathways to show. */
    employeeId: { type: [Number, String], default: null },
    /** The tab is showing — fetch only while it is. */
    active: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      error: '',
      summary: { pass: 0, open: 0, fail: 0 },
      pathways: [],
      expanded: [],
      loadedFor: null,
    };
  },
  watch: {
    active: { immediate: true, handler() { this.maybeFetch(); } },
    employeeId() {
      // A different user: drop what is on screen before fetching theirs.
      this.loadedFor = null;
      this.pathways = [];
      this.expanded = [];
      this.maybeFetch();
    },
  },
  methods: {
    maybeFetch() {
      if (!this.active || !this.employeeId) return;
      if (this.loadedFor === this.employeeId) return;
      this.fetch();
    },
    fetch() {
      const id = this.employeeId;
      this.loading = true;
      this.error = '';
      this.$http
        .get('safedig/employees/' + id + '/pathways')
        .then(resp => {
          // A slow response for a user the admin has since moved off.
          if (this.employeeId !== id) return;
          const data = resp.data || {};
          this.summary = data.summary || { pass: 0, open: 0, fail: 0 };
          this.pathways = Array.isArray(data.pathways) ? data.pathways : [];
          this.loadedFor = id;
        })
        .catch(() => { this.error = 'Could not load this user’s pathways.'; })
        .finally(() => { this.loading = false; });
    },
    isOpen(id) { return this.expanded.indexOf(id) !== -1; },
    toggle(id) {
      const at = this.expanded.indexOf(id);
      if (at === -1) this.expanded.push(id);
      else this.expanded.splice(at, 1);
    },
    /** Which colour a module's own course result gets. */
    moduleResult(mod) {
      if (mod.course_status === 1) return 'pass';
      if (mod.course_status === 0) return 'fail';
      if (mod.course_status === 3) return 'expired';
      return 'open';
    },
    shortDate(value) {
      if (!value) return '—';
      const d = moment(value);
      return d.isValid() ? d.format('MM-DD-YYYY') : '—';
    },
  },
};
</script>

<style scoped>
.ppp {
  --ppp-accent: #3e7799;
  --ppp-text: #0f172a;
  --ppp-muted: #64748b;
  --ppp-border: #e2e8f0;
  --ppp-pass: #16a34a;
  --ppp-open: #d97706;
  --ppp-fail: #dc2626;

  color: var(--ppp-text);
  font-size: 0.8125rem;
}

.ppp-state {
  padding: 2.5rem 1rem;
  text-align: center;
  color: var(--ppp-muted);
}
.ppp-state--error { color: var(--ppp-fail); }
.ppp-spinner {
  display: inline-block;
  width: 22px;
  height: 22px;
  border: 3px solid var(--ppp-border);
  border-top-color: var(--ppp-accent);
  border-radius: 50%;
  animation: ppp-spin 0.8s linear infinite;
}
@keyframes ppp-spin { to { transform: rotate(360deg); } }

/* Summary cards ───────────────────────────────────────────── */
.ppp-cards {
  display: grid;
  /* Follows the panel's own width, not the viewport's — it lives in a modal. */
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}
.ppp-card {
  border: 1px solid var(--ppp-border);
  border-radius: 12px;
  padding: 0.85rem 1rem;
  background: #fff;
}
.ppp-card__label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--ppp-muted);
}
.ppp-card__value {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
  margin-top: 0.15rem;
}

.ppp-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
  background: var(--ppp-muted);
}
.ppp-dot--pass { background: var(--ppp-pass); }
.ppp-dot--open { background: var(--ppp-open); }
.ppp-dot--fail, .ppp-dot--expired { background: var(--ppp-fail); }

/* Section head ────────────────────────────────────────────── */
.ppp-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.6rem;
}
.ppp-head__title { font-size: 0.9375rem; font-weight: 700; margin: 0; }
.ppp-head__sub { font-size: 0.75rem; color: var(--ppp-muted); margin: 0.15rem 0 0; }
.ppp-head__meta { font-size: 0.75rem; color: var(--ppp-muted); }

/* Pathway rows ────────────────────────────────────────────── */
.ppp-list { list-style: none; margin: 0; padding: 0; }
.ppp-row {
  border: 1px solid var(--ppp-border);
  border-radius: 12px;
  background: #fff;
  margin-bottom: 0.6rem;
  overflow: hidden;
}
.ppp-row__head {
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem 0.85rem;
  padding: 0.85rem 1rem;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.ppp-row__head:hover { background: #f8fafc; }
.ppp-chev { color: var(--ppp-muted); flex-shrink: 0; transition: transform 0.15s ease; }
.ppp-chev--open { transform: rotate(90deg); }

.ppp-row__main { flex: 1 1 220px; min-width: 0; }
.ppp-row__title {
  display: block;
  font-weight: 700;
  color: var(--ppp-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ppp-row__sub { display: block; font-size: 0.75rem; color: var(--ppp-muted); margin-top: 0.1rem; }

.ppp-row__progress { flex: 1 1 190px; min-width: 150px; }
.ppp-row__counts {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--ppp-muted);
  margin-bottom: 0.3rem;
}
.ppp-row__counts strong { color: var(--ppp-text); }
.ppp-bar {
  display: block;
  height: 7px;
  border-radius: 99px;
  background: #e2e8f0;
  overflow: hidden;
}
.ppp-bar__fill { display: block; height: 100%; border-radius: 99px; background: var(--ppp-accent); }
.ppp-bar__fill--pass { background: var(--ppp-pass); }
.ppp-bar__fill--open { background: var(--ppp-open); }
.ppp-bar__fill--fail, .ppp-bar__fill--expired { background: var(--ppp-fail); }

.ppp-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
  margin-left: auto;
  padding: 0.25rem 0.6rem;
  border-radius: 99px;
  font-size: 0.75rem;
  font-weight: 700;
}
.ppp-pill--pass { background: rgba(22, 163, 74, 0.12); color: #15803d; }
.ppp-pill--open { background: rgba(217, 119, 6, 0.12); color: #b45309; }
.ppp-pill--fail, .ppp-pill--expired { background: rgba(220, 38, 38, 0.12); color: #b91c1c; }

/* Module drop-down ────────────────────────────────────────── */
.ppp-modules { border-top: 1px solid var(--ppp-border); background: #f8fafc; padding: 0.6rem 1rem 0.85rem; }
.ppp-modules__empty { padding: 0.75rem 0; color: var(--ppp-muted); }
.ppp-modules__scroll { overflow-x: auto; }
.ppp-row--removed > td { background: #fbfbfc; }
.ppp-removed {
  display: block;
  margin-top: 0.15rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--ppp-muted);
}
.ppp-table { width: 100%; min-width: 520px; border-collapse: collapse; }
.ppp-table th {
  text-align: left;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ppp-muted);
  font-weight: 700;
  padding: 0.35rem 0.5rem;
  border-bottom: 1px solid var(--ppp-border);
}
.ppp-table td { padding: 0.45rem 0.5rem; border-bottom: 1px solid #eef2f6; vertical-align: middle; }
.ppp-table tr:last-child td { border-bottom: 0; }
.ppp-table__seq { width: 34px; color: var(--ppp-muted); }

.ppp-tag {
  display: inline-block;
  padding: 0.1rem 0.5rem;
  border-radius: 99px;
  font-size: 0.6875rem;
  font-weight: 700;
}
.ppp-tag--pass { background: rgba(22, 163, 74, 0.12); color: #15803d; }
.ppp-tag--open { background: rgba(217, 119, 6, 0.12); color: #b45309; }
.ppp-tag--fail, .ppp-tag--expired { background: rgba(220, 38, 38, 0.12); color: #b91c1c; }

.ppp-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  margin-top: 0.9rem;
  font-size: 0.75rem;
  color: var(--ppp-muted);
}
.ppp-legend span { display: inline-flex; align-items: center; gap: 0.4rem; }

@media (max-width: 600px) {
  .ppp-legend { gap: 0.5rem 1rem; }
}
</style>
